import {
  formatDateToShortDate,
  getField,
  Locales,
  QUESTION_TYPES_WITH_CHOICES,
  QuestionType,
  Utils,
} from "@simplyk/common";

import { AnswerSeparator } from "@/common/constants/CustomAnswers";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { QuestionOutput } from "@/common/types/trpc";

type ParticipantAnswer = NonNullable<TicketingPaymentInput["customAnswersOfParticipants"]>[number]["answers"][number];

const toTrimmedString = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const formatSingleAnswer = (answer: ParticipantAnswer, question: QuestionOutput, locale: Locales): string => {
  if (QUESTION_TYPES_WITH_CHOICES.includes(question.type)) {
    const selectedIds = answer.choiceIds ?? [];
    if (selectedIds.length === 0) {
      return "";
    }
    const labels = (question.choices ?? [])
      .filter((choice) => selectedIds.includes(choice.id))
      .map((choice) => getField(choice.choiceFields, locale)?.body ?? "")
      .filter(Utils.notEmpty);

    return labels.join(AnswerSeparator);
  }

  if (question.type === QuestionType.Name) {
    const first = toTrimmedString(answer.answers?.[0]);
    const last = toTrimmedString(answer.answers?.[1]);
    return [first, last].filter(Boolean).join(" ");
  }

  if (question.type === QuestionType.Date) {
    const value: unknown = answer.answers?.[0];
    if (value instanceof Date) {
      return formatDateToShortDate(value, locale);
    }
    const trimmed = toTrimmedString(value);
    return trimmed ? formatDateToShortDate(trimmed, locale) : "";
  }

  return toTrimmedString(answer.answers?.[0]);
};

/**
 * Build the bullet-list lines we show under a ticket row in the shop cart.
 * Returns one entry per non-empty answer; questions without an answer are omitted.
 */
export const formatParticipantAnswers = (
  participant: NonNullable<TicketingPaymentInput["customAnswersOfParticipants"]>[number] | undefined,
  rateQuestions: QuestionOutput[],
  locale: Locales
): string[] => {
  if (!participant?.answers || rateQuestions.length === 0) {
    return [];
  }

  const lines: string[] = [];
  for (const answer of participant.answers) {
    const question = rateQuestions.find((q) => q.id === answer.questionId);
    if (!question) {
      continue;
    }
    const formatted = formatSingleAnswer(answer, question, locale);
    if (formatted) {
      lines.push(formatted);
    }
  }
  return lines;
};
